import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@components/common/popover'
import { Button } from '@components/common/button'
import useTranslator from '@pma/hooks/use-translator'
import { Label } from '../label'
import { Separator } from '../separator'
import AddTaskTableColumn from '@pma/components/projects/add-task-table-column'
import { Check, X } from 'lucide-react'
import { DictType } from '@pma/types'
import { cn } from '@pma/utils/helpers'
import { MixerHorizontalIcon } from '@radix-ui/react-icons'
import { Table } from '@tanstack/react-table'
import { useEffect } from 'react'
import { useCustomColumns } from '@pma/stores/tasks-store'
import { useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { parseCookies } from 'nookies'
import { ACCESS_TOKEN } from '@pma/utils/constants'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from '@pma/hooks/use-toast'
import {
  DELETE_CUSTOM_COLUMNS_ENDPOINT,
  SERVER_URL,
} from '@pma/utils/api-routes'

interface DataTableViewOptionsProps<TData> {
  table: Table<TData>
  columns: Array<DictType>
  context: string
  projectId?: string
}

const tableColumns = [{ id: 'id', title: 'Ticket' }, { id: 'created_by', title: 'Created By' }, { id: 'title', title: 'Title' }, { id: 'notes', title: 'Notes' }, { id: 'description', title: 'Description' }, { id: 'status', title: 'Status' }, { id: 'priority', title: 'Priority' }, { id: 'responsible', title: 'Assigned' }, { id: '_gis', title: 'Gis' }, { id: 'files', title: 'Files' }, { id: 'Duration', title: 'Duration' }, { id: 'sessions', title: 'Time' },]

export function DataTableViewOptions<TData>({
  table,
  columns,
  context,
  projectId = '',
}: DataTableViewOptionsProps<TData>) {
  const { translate, locale } = useTranslator()

  const { customColumns, setCustomColumns } = useCustomColumns()
  const navigate = useNavigate()

  const [searchParam] = useSearchParams()
  const newParams = new URLSearchParams(searchParam)

  const accessToken = parseCookies()[ACCESS_TOKEN]

  const isHebrew = locale === 'he'

  const getColumnTitle = (colId: string) => {
    const find = tableColumns.find((val) => val.id === colId)
    let title = ''
    if (find) {
      title = find.title
    } else {
      title = colId
    }
    return title
  }
  const deleteCustomColumn = async (columnId: string) => {
    const response = await axios.delete(
      SERVER_URL + DELETE_CUSTOM_COLUMNS_ENDPOINT + `/${columnId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    )
    return response.data
  }

  const deleteColumnMutation = useMutation({
    mutationKey: ['delete-custom-column'],
    mutationFn: deleteCustomColumn,
    onSuccess: (data) => {
      newParams.set('revalidate', 'projects')
      navigate(`?${newParams.toString()}`)
      setCustomColumns(data?._custom_columns)
    },
    onError: (error) => {
      toast({
        title: translate('Error'),
        description: translate('Unable to delete column'),
        variant: 'error',
      })
    },
  })
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          size='sm'
          className='ml-auto hidden h-8 lg:flex'
        >
          <MixerHorizontalIcon className='mr-2 h-4 w-4' />
          {translate('Columns')}
        </Button>
      </PopoverTrigger>
      <PopoverContent align='end' className='md:w-[350px]'>
        <Label>{translate('Toggle Columns')}</Label>
        <Separator />
        <div className='flex flex-wrap gap-4 py-1'>
          {table
            .getAllColumns()
            .filter(
              (column) =>
                typeof column.accessorFn !== 'undefined' && column.getCanHide(),
            )
            .map((column) => {
              return (
                <span
                  key={column.id}
                  className='capitalize text-sm flex items-center gap-1 cursor-pointer'
                  onClick={() => {
                    const isVisible = column.getIsVisible()
                    column.toggleVisibility(!isVisible)
                  }}
                >
                  {translate(getColumnTitle(column?.id).replaceAll('_', ' ').replaceAll('.', ' ').trim())}
                  {column.getIsVisible() ? (
                    <div className='h-3 w-3 flex items-center justify-center bg-success rounded-full'>
                      <Check size={10} />
                    </div>
                  ) : (
                    <div className='h-3 w-3 flex items-center text-white justify-center bg-slate-500 rounded-full'>
                      <Check size={10} />
                    </div>
                  )}
                </span>
              )
            })}
        </div>

        <Separator />
        {context === 'tasks' && (
          <div className='py-1 bg-gray-100 p-1 rounded-md'>
            <h3 className='text-sm font-semibold'>
              {translate('Custom Columns')}
            </h3>
            <div className='flex flex-wrap gap-4 py-1'>
              {customColumns.map((column) => {
                return (
                  <span
                    key={column.id}
                    className='border rounded-md p-1 capitalize text-sm flex items-center gap-1 cursor-pointer'
                  >
                    {translate(column.name)}
                    <div className='h-3 w-3 flex items-center text-white justify-center bg-red-500 rounded-full'>
                      <X
                        size={10}
                        onClick={() => {
                          deleteColumnMutation.mutate(column?.id)
                        }}
                      />
                    </div>
                  </span>
                )
              })}
            </div>
            <Separator />

            <div
              className={cn(
                'flex items-center justify-end py-1',
                isHebrew ? 'rtl' : 'ltr',
              )}
            >
              <AddTaskTableColumn context={context} projectId={projectId} />
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
